import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import type { PaymentSession } from './payments.service';

/** Phase V1.3 — result of starting a booking payment (fee + refundable deposit, in øre). */
export interface BookingPayResult {
  payment: PaymentSession | null;
  feeMinor: number;
  depositMinor: number;
}

export interface ResourceCard {
  id: string;
  name: string;
  type: string;
  imageKey?: string | null;
  description: string;
  capacity: number;
  accessMode: string;
  /** 2026-09 redesign: shown on the overview card. */
  price?: PriceView | null;
  maxDurationMinutes: number;
  /** Today's confirmed bookings (UTC ranges) for the day strip. */
  todayBusy: BusyRange[];
}

export interface ExternalServiceCard {
  label: string;
  url: string;
}

export interface BookingOverview {
  interne: ResourceCard[];
  eksterne: ExternalServiceCard[];
}

export interface PriceView {
  amount: number;
  deposit: number;
  currency: string;
}

export interface OpeningHoursView {
  weekday: number;
  startMinute: number;
  endMinute: number;
}

export interface BookingRulesView {
  maxDurationMinutes: number;
  maxActivePerHousehold: number;
  quarantineMinutes: number;
  whoCanBook: string;
  openingHours: OpeningHoursView[];
  seasonFromUtc?: string | null;
  seasonToUtc?: string | null;
  slotGranularityMinutes: number;
}

export interface ResourceDetail {
  id: string;
  name: string;
  type: string;
  imageKey?: string | null;
  description: string;
  capacity: number;
  accessMode: string;
  rules: BookingRulesView;
  price?: PriceView | null;
}

export interface BusyRange {
  startUtc: string;
  endUtc: string;
}

export interface HeldRange {
  startUtc: string;
  endUtc: string;
  mine: boolean;
}

export interface CalendarView {
  busy: BusyRange[];
  held: HeldRange[];
}

export interface HoldResult {
  allowed: boolean;
  reason?: string | null;
  holdId?: string | null;
  expiresAtUtc?: string | null;
}

export interface BookingView {
  id: string;
  resourceId: string;
  resourceName: string;
  startUtc: string;
  endUtc: string;
  status: string;
}

export interface ConfirmResult {
  outcome: 'confirmed' | 'conflict' | 'denied';
  reason?: string | null;
  booking?: BookingView | null;
}

export interface GuestParkingResult {
  status: 'granted' | 'waitlisted';
  assignedSlotIndex?: number | null;
}

export interface SlotInput {
  startUtc: string;
  endUtc: string;
}

export interface BookingRulesInput {
  maxDurationMinutes: number;
  maxActivePerHousehold: number;
  quarantineMinutes: number;
  whoCanBook: string;
  openingHours: OpeningHoursView[];
  seasonFromUtc?: string | null;
  seasonToUtc?: string | null;
  slotGranularityMinutes: number;
}

export interface CreateResourceInput {
  name: string;
  type: string;
  description: string;
  imageKey?: string | null;
  capacity: number;
  accessMode: string;
  rules: BookingRulesInput;
  price?: PriceView | null;
}

// ── SignalR BookingHub contract (the typed hub-event module the live calendar consumes) ──

/** The hub endpoint path (JWT supplied via the `access_token` query param). */
export const BOOKING_HUB_PATH = '/hubs/booking';

/** The client method name carrying calendar deltas. */
export const BOOKING_CALENDAR_DELTA_EVENT = 'calendarDelta';

export interface CalendarDelta {
  resourceId: string;
  kind: 'held' | 'released' | 'confirmed';
  startUtc: string;
  endUtc: string;
}

/**
 * Booking client (`/api/booking`). Hand-written to mirror the Phase M5 backend contract.
 * The rule engine + the no-double-booking transaction are enforced server-side; this client
 * just places holds, confirms, and reads calendars. The live calendar merges
 * {@link CalendarView} with {@link CalendarDelta} events from {@link BOOKING_HUB_PATH}.
 */
@Injectable({ providedIn: 'root' })
export class BookingService {
  private readonly http = inject(HttpClient);

  placeHold(resourceId: string, slot: SlotInput): Observable<HoldResult> {
    return this.http.post<HoldResult>(`/api/booking/ressurser/${encodeURIComponent(resourceId)}/hold`, slot);
  }

  confirm(resourceId: string, holdId: string, slot: SlotInput): Observable<ConfirmResult> {
    return this.http.post<ConfirmResult>('/api/booking/bekreft', { resourceId, holdId, ...slot });
  }

  cancel(id: string): Observable<void> {
    return this.http.delete<void>(`/api/booking/${encodeURIComponent(id)}`);
  }

  // ── Phase V1.3: payment, deposit, refund & condition reports ──

  /** Start payment for a confirmed booking (fee + deposit derived server-side). */
  pay(id: string): Observable<BookingPayResult> {
    return this.http.post<BookingPayResult>(`/api/booking/${encodeURIComponent(id)}/betal`, {});
  }

}
